import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, throwError } from 'rxjs';
import { AppErrors } from '@fitness-force/errors';
import {
  ProductResponse,
  CreateProductRequest,
  UpdateProductRequest,
  ProductResponsePagedResults,
  ListProductsFilterRequest,
  GetProductsByIdsRequest,
  CreateResponseData,
  UpdateResponseData
} from '../models';
import { environment } from 'apps/ff-frontend/src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ProductService {
  private readonly API_BASE_URL = environment.RETAIL_BILLING_URL;

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  constructor(private http: HttpClient) {}


  // Company-level product operations
  getCompanyProducts(requestParams: Map<string, any>): Observable<ProductResponsePagedResults> {
    let parsedParams = new HttpParams()
    requestParams.forEach((p: any) => {
      parsedParams = parsedParams.append(p.name, p.value);
    });
    
    
    return this.http.get<ProductResponsePagedResults>(`${this.API_BASE_URL}/companies/retail_products`, {headers: this.httpOptions.headers, params: parsedParams}).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Product API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  getCompanyProduct(productId: number): Observable<ProductResponse> {

    return this.http.get<ProductResponse>(`${this.API_BASE_URL}/companies/retail_products/${productId}`, {headers: this.httpOptions.headers}).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Get Company Product API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  createCompanyProduct(request: CreateProductRequest): Observable<CreateResponseData> {
    return this.http.post<CreateResponseData>(`${this.API_BASE_URL}/companies/retail_products`, request, {headers: this.httpOptions.headers}).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Create Company Product API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  updateCompanyProduct(productId: number, request: UpdateProductRequest): Observable<UpdateResponseData> {
    return this.http.put<UpdateResponseData>(`${this.API_BASE_URL}/companies/retail_products/${productId}`, request, {headers: this.httpOptions.headers}).pipe(
      map((res: any) => res),
      catchError((error: Response) => {
        console.error('Update Company Product API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  // Tenant-level product operations
  getTenantProducts(tenantId: number, requestParams: Map<string, any>): Observable<ProductResponsePagedResults> {
    let parsedParams = new HttpParams()
    requestParams.forEach((p: any) => {
      parsedParams = parsedParams.append(p.name, p.value);
    });
  

    return this.http.get<ProductResponsePagedResults>(`${this.API_BASE_URL}/tenants/${tenantId}/retail_products`, {headers: this.httpOptions.headers, params: parsedParams}).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Get Tenant Products API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  getTenantProduct(tenantId: number, productId: number): Observable<ProductResponse> {
    return this.http.get<ProductResponse>(`${this.API_BASE_URL}/tenants/${tenantId}/retail_products/${productId}`, {headers: this.httpOptions.headers}).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Get Tenant Product API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  createTenantProduct(tenantId: number, request: CreateProductRequest): Observable<CreateResponseData> {
    return this.http.post<CreateResponseData>(`${this.API_BASE_URL}/tenants/${tenantId}/retail_products`, request, {headers: this.httpOptions.headers}).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Create Tenant Product API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  updateTenantProduct(tenantId: number, productId: number, request: UpdateProductRequest): Observable<UpdateResponseData> {
    return this.http.put<UpdateResponseData>(`${this.API_BASE_URL}/tenants/${tenantId}/retail_products/${productId}`, request, {headers: this.httpOptions.headers}).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Update Tenant Product API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  // Legacy operations for backward compatibility
  getProduct(tenantId: number, productId: number): Observable<ProductResponse> {
    return this.http.get<ProductResponse>(`${this.API_BASE_URL}/tenants/${tenantId}/products/${productId}`, {headers: this.httpOptions.headers}).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Get Product API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  getProductsForPlan(tenantId: number, request: GetProductsByIdsRequest): Observable<ProductResponse[]> {
    return this.http.post<ProductResponse[]>(`${this.API_BASE_URL}/tenants/${tenantId}/products/get-for-plan`, request, {headers: this.httpOptions.headers}).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Get Products For Plan API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }
}